import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FlaskIcon, PaperPlaneTiltIcon } from '@phosphor-icons/react';
import {
  SEVERITIES,
  type Channel,
  type ChannelTestResponse,
  type DeliveryMode,
  type DeliveryModePreview,
  type Severity,
} from '@meshalert/contract';
import { ApiError, api } from '../lib/api.ts';
import { CHANNEL_LABEL, DELIVERY_MODE_LABEL, shortTime } from '../lib/format.ts';
import { PageHeader } from '../components/Layout.tsx';
import { useSession } from '../lib/session.tsx';
import {
  Button,
  EmptyState,
  ErrorState,
  ListSkeleton,
  Panel,
  Select,
  SeverityChip,
  Tabs,
  cx,
  severityRuleClass,
} from '../components/ui.tsx';

type Loudness = 'auto' | DeliveryMode;

const LOUDNESS_ITEMS: { value: Loudness; label: string }[] = [
  { value: 'auto', label: 'Wie echter Alarm' },
  { value: 'silent', label: 'Stumm' },
  { value: 'normal', label: 'Normale Push' },
  { value: 'critical', label: 'Alarmton' },
];

const LOUDNESS_HINT: Record<Loudness, string> = {
  auto: 'Der Server entscheidet wie im Ernstfall: Schwelle des Geräts, Focus und Ruhezeiten.',
  silent: 'Landet lautlos in der Mitteilungszentrale, egal welcher Schweregrad.',
  normal: 'Banner mit Ton. Der Stummschalter bleibt wirksam.',
  critical: 'Klingelt auch bei stummem Telefon und aktivem Focus. Nur senden, wenn das gerade passt.',
};

const SEVERITY_TEXT: Record<Severity, string> = {
  info: 'Hinweis ohne Handlungsbedarf.',
  warning: 'Etwas läuft aus dem Ruder, noch ohne Ausfall.',
  error: 'Ein Dienst ist gestört.',
  critical: 'Ausfall, jemand muss sofort ran.',
};

/** Pause zwischen den Stufen beim Durchlauf, damit sich die Töne nicht überlagern. */
const SEQUENCE_GAP_MS = 2500;

type LogEntry = {
  key: string;
  at: string;
  channel: string;
  severity: Severity;
  mode: DeliveryMode | null;
  ok: boolean;
  detail: string;
};

const isPush = (channel: Channel | undefined) =>
  channel?.kind === 'apns_ios' || channel?.kind === 'apns_macos';

/**
 * Der Ort, an dem man vor dem Ernstfall hört, wie jeder Schweregrad klingt.
 * Gesendet wird über denselben Weg wie ein echter Alarm, aber ohne Eintrag in
 * der Alarmliste und ohne Weiterleitungsregeln: das Ziel ist genau ein Kanal.
 */
export function TestPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [channelId, setChannelId] = useState('');
  const [loudness, setLoudness] = useState<Loudness>('auto');
  const [log, setLog] = useState<LogEntry[]>([]);
  const [sequence, setSequence] = useState<Severity | null>(null);

  const channels = useQuery({ queryKey: ['channels'], queryFn: () => api.channels.list({ limit: 100 }) });
  const items = channels.data?.items ?? [];
  const selected = items.find((c) => c.id === channelId);

  // Vorauswahl: das eigene aktive Gerät, weil man meistens sich selbst testet.
  useEffect(() => {
    if (channelId || items.length === 0) return;
    const preferred =
      items.find((c) => c.is_active && c.user_id === user?.id && isPush(c)) ??
      items.find((c) => c.is_active) ??
      items[0];
    if (preferred) setChannelId(preferred.id);
  }, [items, channelId, user?.id]);

  const preview = useQuery({
    queryKey: ['delivery-mode-preview', channelId],
    queryFn: async () => {
      const entries = await Promise.all(
        SEVERITIES.map(async (s) => [s, await api.devices.previewMode(channelId, s)] as const),
      );
      return Object.fromEntries(entries) as Record<Severity, DeliveryModePreview>;
    },
    enabled: loudness === 'auto' && isPush(selected),
  });

  const record = (severity: Severity, result: ChannelTestResponse | Error) => {
    const entry: LogEntry =
      result instanceof Error
        ? {
            key: crypto.randomUUID(),
            at: new Date().toISOString(),
            channel: selected?.label ?? '',
            severity,
            mode: null,
            ok: false,
            detail: result instanceof ApiError ? result.message : 'Anfrage fehlgeschlagen',
          }
        : {
            key: crypto.randomUUID(),
            at: new Date().toISOString(),
            channel: selected?.label ?? '',
            severity,
            mode: result.mode,
            ok: result.delivered,
            detail: result.delivered ? result.reason : result.detail,
          };
    setLog((current) => [entry, ...current].slice(0, 50));
  };

  const send = useMutation({
    mutationFn: (severity: Severity) =>
      api.channels.test(channelId, {
        severity,
        mode: loudness === 'auto' ? null : loudness,
      }),
    onSuccess: (result, severity) => record(severity, result),
    onError: (error, severity) => record(severity, error),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['channels'] }),
  });

  const sendAll = async () => {
    for (const [index, severity] of SEVERITIES.entries()) {
      setSequence(severity);
      try {
        await send.mutateAsync(severity);
      } catch {
        // Schon im Verlauf vermerkt. Die übrigen Stufen trotzdem senden.
      }
      if (index < SEVERITIES.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, SEQUENCE_GAP_MS));
      }
    }
    setSequence(null);
  };

  const busy = send.isPending || sequence !== null;

  const expected = (severity: Severity): string => {
    if (!selected) return '';
    if (!isPush(selected)) return 'Webhook, ohne Lautstärke';
    if (loudness !== 'auto') return DELIVERY_MODE_LABEL[loudness];
    const decision = preview.data?.[severity];
    return decision ? DELIVERY_MODE_LABEL[decision.mode] : '...';
  };

  return (
    <>
      <PageHeader
        title="Testbereich"
        description="Echte Testalarme an genau einen Kanal, in jedem Schweregrad. Sie erscheinen nicht in der Alarmliste und laufen an den Weiterleitungsregeln vorbei."
        action={
          <Button
            variant="primary"
            icon={<PaperPlaneTiltIcon size={15} />}
            disabled={!selected || busy}
            onClick={() => void sendAll()}
          >
            {sequence ? `Sendet ${SEVERITIES.indexOf(sequence) + 1} von 4` : 'Alle vier senden'}
          </Button>
        }
      />

      {channels.isLoading ? (
        <Panel bodyClassName="p-0">
          <ListSkeleton rows={2} />
        </Panel>
      ) : channels.isError ? (
        <Panel bodyClassName="p-0">
          <ErrorState message="Die Kanäle konnten nicht geladen werden." onRetry={() => channels.refetch()} />
        </Panel>
      ) : items.length === 0 ? (
        <Panel bodyClassName="p-0">
          <EmptyState
            icon={<FlaskIcon size={26} weight="light" />}
            title="Kein Kanal zum Testen"
            description="Melde dich in der iOS-App an, dann registriert sich das Telefon als Kanal. Webhooks legst du unter Kanäle an."
            action={
              <Button size="sm" onClick={() => navigate('/kanaele')}>
                Zu den Kanälen
              </Button>
            }
          />
        </Panel>
      ) : (
        <div className="space-y-5">
          <Panel>
            <div className="grid gap-5 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="test-channel" className="text-[13px] font-medium text-ink">
                  Kanal
                </label>
                <Select id="test-channel" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
                  {items.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label} ({CHANNEL_LABEL[c.kind]}, {c.user_name}){c.is_active ? '' : ', deaktiviert'}
                    </option>
                  ))}
                </Select>
                {selected?.last_error ? (
                  <p className="font-mono text-[11.5px] leading-relaxed text-critical">
                    Letzter Fehler: {selected.last_error}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <span id="test-loudness" className="text-[13px] font-medium text-ink">
                  Lautstärke
                </span>
                <div aria-labelledby="test-loudness" className="overflow-x-auto">
                  <Tabs value={loudness} onChange={setLoudness} items={LOUDNESS_ITEMS} />
                </div>
                <p className={cx('text-[12px]', loudness === 'critical' ? 'text-warning' : 'text-ink-3')}>
                  {isPush(selected) ? LOUDNESS_HINT[loudness] : 'Ein Webhook kennt keine Lautstärke, er bekommt den Schweregrad im Payload.'}
                </p>
              </div>
            </div>
          </Panel>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {SEVERITIES.map((severity) => {
              const decision = loudness === 'auto' ? preview.data?.[severity] : undefined;
              // Nur solange die Anfrage läuft, nicht in der Pause des Durchlaufs.
              const active = send.isPending && send.variables === severity;
              return (
                <section
                  key={severity}
                  className={cx(
                    'relative flex flex-col overflow-hidden rounded-lg border bg-raised shadow-[var(--shadow-card)] transition-colors',
                    active ? 'border-line-strong' : 'border-line',
                  )}
                >
                  <span aria-hidden className={cx('h-1 w-full', severityRuleClass(severity))} />
                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div>
                      <SeverityChip severity={severity} />
                    </div>
                    <p className="text-[13px] leading-relaxed text-ink-2">{SEVERITY_TEXT[severity]}</p>
                    <div className="mt-auto space-y-0.5">
                      <p className="text-[12px] text-ink-3">
                        Kommt an als <span className="font-medium text-ink">{expected(severity)}</span>
                      </p>
                      {decision ? (
                        <p className="text-[11.5px] leading-snug text-ink-3">{decision.reason}</p>
                      ) : null}
                    </div>
                    <Button
                      size="sm"
                      variant={severity === 'critical' ? 'danger' : 'secondary'}
                      icon={<PaperPlaneTiltIcon size={14} />}
                      disabled={!selected || busy}
                      onClick={() => send.mutate(severity)}
                    >
                      {active ? 'Wird gesendet' : 'Senden'}
                    </Button>
                  </div>
                </section>
              );
            })}
          </div>

          <Panel
            title="Verlauf"
            bodyClassName="p-0"
            action={
              log.length > 0 ? (
                <Button size="sm" variant="ghost" onClick={() => setLog([])}>
                  Leeren
                </Button>
              ) : null
            }
          >
            {log.length === 0 ? (
              <p className="px-4 py-8 text-center text-[13px] text-ink-3">
                Noch nichts gesendet. Der Verlauf gilt nur für diese Sitzung im Browser.
              </p>
            ) : (
              <ul role="log" className="divide-y divide-[var(--border-subtle)]">
                {log.map((entry) => (
                  <li key={entry.key} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-4 py-3">
                    <span className="tnum w-11 shrink-0 pt-0.5 text-[12px] text-ink-3">{shortTime(entry.at)}</span>
                    <span className="w-16 shrink-0">
                      <SeverityChip severity={entry.severity} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-ink">
                        {entry.channel}
                        {entry.mode ? (
                          <span className="text-ink-3"> als {DELIVERY_MODE_LABEL[entry.mode]}</span>
                        ) : null}
                      </p>
                      <p
                        className={cx(
                          'mt-0.5 text-[12px] leading-relaxed',
                          entry.ok ? 'text-ink-3' : 'font-mono text-critical',
                        )}
                      >
                        {entry.detail}
                      </p>
                    </div>
                    <span
                      className={cx(
                        'shrink-0 text-[12px] font-medium',
                        entry.ok ? 'text-accent' : 'text-critical',
                      )}
                    >
                      {entry.ok ? 'Angenommen' : 'Fehlgeschlagen'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      )}
    </>
  );
}
